
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Candidate } from "../../types";
import type { RejectionReason } from "../constants/rejectionReasons";

interface UseRejectedCandidateFormProps {
  candidate: Candidate;
  onSave: (updatedCandidate: Candidate) => void;
  onClose: () => void;
}

export const useRejectedCandidateForm = ({ candidate, onSave, onClose }: UseRejectedCandidateFormProps) => {
  const [selectedReasons, setSelectedReasons] = useState<RejectionReason[]>([]);
  const [otherReason, setOtherReason] = useState("");
  const [rejectionNotes, setRejectionNotes] = useState("");
  const [rejectedDate, setRejectedDate] = useState<Date>(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReasonToggle = (reason: RejectionReason) => {
    setSelectedReasons(prev => 
      prev.includes(reason) 
        ? prev.filter(r => r !== reason)
        : [...prev, reason]
    );
    
    // Clear other reason text if "Other" is deselected
    if (reason === "Other" && selectedReasons.includes("Other")) {
      setOtherReason("");
    }
  };

  const validateForm = () => {
    if (selectedReasons.length === 0) {
      toast.error("Please select at least one rejection reason");
      return false;
    }
    
    if (selectedReasons.includes("Other") && !otherReason.trim()) {
      toast.error("Please provide a custom reason when selecting 'Other'");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Prepare rejection reasons array, including custom reason if "Other" is selected
      const finalReasons = selectedReasons.includes("Other") 
        ? [...selectedReasons.filter(r => r !== "Other"), otherReason.trim()]
        : selectedReasons;

      const updateData = {
        status: "rejected",
        rejection_reasons: finalReasons, // This will be stored as JSONB array
        rejection_notes: rejectionNotes.trim() || null,
        rejected_date: rejectedDate.toISOString()
      };

      const { data, error } = await supabase
        .from("candidates")
        .update(updateData)
        .eq("id", candidate.id)
        .select()
        .single();

      if (error) throw error;

      if (data) {
        // Properly convert the response data to match our Candidate interface
        const updatedCandidate: Candidate = {
          ...candidate,
          ...updateData,
          rejection_reasons: finalReasons, // Ensure this is a string array
        };
        
        onSave(updatedCandidate);
        toast.success(`${candidate.first_name || 'Candidate'} has been marked as rejected`);
        onClose();
      }
    } catch (error) {
      console.error("Error saving rejection details:", error);
      toast.error("Failed to save rejection details");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    selectedReasons,
    otherReason,
    setOtherReason,
    rejectionNotes,
    setRejectionNotes,
    rejectedDate,
    setRejectedDate,
    handleReasonToggle,
    handleSubmit,
    isSubmitting,
  };
};
